import React, {useCallback, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {WINDOW_MODE_MOBILE_WIDTH, WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {TreeGetSubcategoryData} from 'types'
import {useDidMount, useHistory} from 'utils'
import {
  Button,
  Input,
  List,
  ListItemSelect,
  Menu,
  ModalLoading,
} from 'common/components'
import convertUnit from 'lib/unit'
import {TreeEditTreeFormProps} from './TreeEditFormProps'

const StyledContainer = styled.div`
  width: ${convertUnit(561)};
  height: max-content;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  margin: ${convertUnit(20)} auto;
  padding: ${convertUnit(40)};
  border-radius: ${convertUnit(16)};
  background-color: ${({theme}) => theme.white_1};

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin: auto;
    width: 100%;
    height: 100%;
  }

  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    padding: ${convertUnit(20)};
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: ${convertUnit(20)};
`

export default function TreeFototreeCategoryForm({
  stateTreeInfo,
  stateActiveContent,
}: TreeEditTreeFormProps) {
  const history = useHistory()
  const {translate} = useTranslation()
  const setActiveContent = stateActiveContent[1]
  const [treeInfo, setTreeInfo] = stateTreeInfo
  const [loading, setLoading] = useState(false)
  const [input, setInput] = useState(treeInfo.sub_category)
  const [data, setData] = useState<TreeGetSubcategoryData[]>()
  const stateToggle = useState(false)
  const setToggle = stateToggle[1]

  const form = useForm<{category: TreeGetSubcategoryData}>({
    defaultValues: {
      category: {
        id: '',
        name: treeInfo.sub_category,
      },
    },
  })
  const {setValue, register, watch, handleSubmit} = form
  const {category} = watch()

  const isDisabled = useMemo(
    () =>
      !category.id || !category.name || category.name === treeInfo.sub_category,
    [category, treeInfo.sub_category],
  )

  const handleLoadData = useCallback(() => {
    requestData('tree_get_fototree_subcategory', {
      params: {
        type: treeInfo.category_name,
      },
      onRequestSuccess: ({status, data: {result}}) => {
        if (status === 200) setData(result.details)
      },
    })
  }, [treeInfo.category_name])

  const handlePatchCategory = useCallback(() => {
    setLoading(true)
    requestData('tree_patch_fototree', {
      useDefaultMessage: true,
      actionType: 'execute',
      data: {
        tree_id: treeInfo.id,
        category_id: category.id,
      },
      onRequestReceived: () => setLoading(false),
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          const updatedTreeInfo = {
            ...treeInfo,
            sub_category: category.name,
          }
          setTreeInfo(updatedTreeInfo)
          history.replace(
            'tree_fototree_edit',
            {
              treeId: treeInfo.id,
              data: updatedTreeInfo,
            },
            treeInfo.alias,
          )
          setActiveContent('profile')
        }
      },
    })
  }, [
    category.id,
    category.name,
    history,
    setActiveContent,
    setTreeInfo,
    treeInfo,
  ])

  const handleRenderItem = useCallback(
    (item: TreeGetSubcategoryData) => (
      <ListItemSelect
        title={item.name}
        onClick={() => {
          setToggle(false)
          setValue('category', item)
          setInput(item.name)
        }}
      />
    ),
    [setToggle, setValue],
  )

  const handleRenderList = useMemo(
    () => (
      <List
        data={
          !input
            ? data
            : data?.filter((item) =>
                item.name.toLowerCase().includes(input.toLowerCase()),
              )
        }
        keyExtractor={(item) => item.id}
        renderItem={handleRenderItem}
      />
    ),
    [data, handleRenderItem, input],
  )

  const handleRenderInput = useMemo(
    () => (
      <StyledContainer>
        <Menu
          stateToggle={stateToggle}
          contentContainerStyle={{
            height: convertUnit(250),
            borderRadius: convertUnit(8),
          }}
          renderContent={handleRenderList}>
          <Input
            value={input}
            label={translate('tree:category')}
            placeholder={translate('tree:categoryPlaceholder')}
            rightIcon="delete"
            containerStyle={{marginBottom: convertUnit(4)}}
            onChangeText={(text) => {
              if (text.length < 1) {
                setValue('category', {})
              }
              setInput(text)
              setToggle(true)
            }}
            onFocus={() => setToggle(true)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                setToggle((prev) => !prev)
              }
            }}
            onRightIconClick={() => {
              setInput('')
              setValue('category', {})
            }}
          />
        </Menu>
        <StyledButton
          label={translate('global:save')}
          disabled={isDisabled}
          isLoading={loading}
          onClick={handleSubmit(handlePatchCategory)}
        />
      </StyledContainer>
    ),
    [
      handlePatchCategory,
      handleRenderList,
      handleSubmit,
      input,
      isDisabled,
      loading,
      setToggle,
      setValue,
      stateToggle,
      translate,
    ],
  )

  useDidMount(handleLoadData)

  useDidMount(() => {
    register('category')
  })

  return (
    <>
      <ModalLoading visible={loading} />
      {handleRenderInput}
    </>
  )
}
